package fr.frux.feature.treedetail

import androidx.compose.foundation.layout.Box
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.Delete
import androidx.compose.material.icons.outlined.MoreVert
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import fr.frux.core.designsystem.components.BlockUserDialog
import fr.frux.core.designsystem.components.FruxIconButton
import fr.frux.core.designsystem.components.ReportContentMenuItem
import fr.frux.core.designsystem.components.ReportUserSheet
import fr.frux.core.designsystem.components.UserModerationMenuItems
import fr.frux.core.designsystem.components.UserReportReason
import fr.frux.feature.treedetail.resources.Res
import fr.frux.feature.treedetail.resources.treedetail_menu_delete
import fr.frux.feature.treedetail.resources.treedetail_more_actions_description
import org.jetbrains.compose.resources.stringResource

// Composables de l'ADR 0029 (#515), extraits de TreeDetailScreen.kt : ratchet detekt
// TooManyFunctions sur ce fichier, déjà au plafond de fonctions par fichier.

/**
 * Ouvre une feuille ou un dialogue de modération (ADR 0029, #515) seulement si un compte est
 * connecté, sinon pose l'invite via [TreeDetailViewModel.requestModeration]. Fonction à part
 * (pas une lambda en ligne dans [TreeDetailScreen]) : detekt compte la complexité cyclomatique
 * d'une fonction locale dans celle de la fonction qui la déclare, pas une fonction de fichier.
 */
internal fun requestModerationOverlay(
    viewModel: TreeDetailViewModel,
    show: () -> Unit,
) {
    if (viewModel.requestModeration()) show()
}

/**
 * Feuille de signalement et confirmation de masquage de l'ADR 0029 (#515). Extrait de
 * [TreeDetailScreen] pour sa complexité cyclomatique (detekt CyclomaticComplexMethod).
 */
@Composable
internal fun TreeDetailModerationOverlays(
    creatorName: String,
    showReportUserSheet: Boolean,
    showBlockDialog: Boolean,
    onReportUserSheetDismiss: () -> Unit,
    onReportCreator: (reason: UserReportReason, details: String?) -> Unit,
    onBlockDialogDismiss: () -> Unit,
    onBlockCreatorConfirmed: () -> Unit,
) {
    if (showReportUserSheet) {
        ReportUserSheet(
            username = creatorName,
            onSubmit = onReportCreator,
            onDismiss = onReportUserSheetDismiss,
        )
    }
    if (showBlockDialog) {
        BlockUserDialog(
            username = creatorName,
            onConfirm = onBlockCreatorConfirmed,
            onDismiss = onBlockDialogDismiss,
        )
    }
}

/**
 * Menu d'actions de la top bar : Supprimer pour le seul créateur (la correction passe par le bloc
 * Identité, ouverte à tous), ou les gestes de l'ADR 0029
 * (#515) sur l'arbre d'autrui (signaler la description, signaler ou masquer l'auteur).
 * Extrait de `TreeDetailTopBar` pour sa longueur (detekt LongMethod).
 */
@Composable
internal fun TreeDetailActionsMenu(
    tree: TreeDetail,
    showMenu: Boolean,
    onShowMenuChange: (Boolean) -> Unit,
    onDeleteClick: () -> Unit,
    onReportDescription: () -> Unit,
    onReportCreator: () -> Unit,
    onBlockCreator: () -> Unit,
) {
    val canReportDescription = !tree.isOwnTree && !tree.description.isNullOrBlank()
    if (!tree.isOwnTree && !tree.canModerateCreator && !canReportDescription) return

    Box {
        FruxIconButton(
            icon = Icons.Outlined.MoreVert,
            contentDescription = stringResource(Res.string.treedetail_more_actions_description),
            onClick = { onShowMenuChange(true) },
        )
        DropdownMenu(expanded = showMenu, onDismissRequest = { onShowMenuChange(false) }) {
            if (tree.isOwnTree) {
                DropdownMenuItem(
                    text = { Text(stringResource(Res.string.treedetail_menu_delete)) },
                    leadingIcon = { Icon(Icons.Outlined.Delete, contentDescription = null) },
                    onClick = {
                        onShowMenuChange(false)
                        onDeleteClick()
                    },
                )
            } else {
                if (canReportDescription) {
                    ReportContentMenuItem(onClick = onReportDescription, onDismissMenu = { onShowMenuChange(false) })
                }
                if (tree.canModerateCreator) {
                    // Une fiche ouverte par lien direct sur un compte déjà masqué repropose
                    // « Masquer » : le PUT est idempotent (204).
                    UserModerationMenuItems(
                        username = tree.creatorUsername.orEmpty(),
                        isBlocked = false,
                        onReportUser = onReportCreator,
                        onBlock = onBlockCreator,
                        onUnblock = {},
                        onDismissMenu = { onShowMenuChange(false) },
                    )
                }
            }
        }
    }
}
